import React, { useState } from 'react';
import {
    Box,
    Divider
} from '@mui/material';
import ContactDetailMenu from './ContactDetailMenu';
import ContactDetailCard from './ContactDetailCard';
import ContactDetailsEdit from 'components/contacts/contactDetails/editContactDetails/ContactDetailsEdit';
import { EditOutlinedIcon, SaveAltOutlinedIcon, EmailOutlinedIcon } from 'icons/materialIcons';
import SendLeadEmailModal from 'components/contacts/contactDetails/SendLeadEmailModal';
import { useNavigate } from 'react-router-dom';
import { downloadVCard } from 'utils/vcfUtils';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';
import { useLeadCaptureContext } from 'contexts/LeadCaptureContext'; // Use the lead capture context
import ExportUtils from 'utils/contacts/ExportUtils'; // Import the export utility

const ContactDetailsContainer = ({ contact, onBack, onDeleteComplete, startInEditMode = false }) => {
    const [isEditing, setIsEditing] = useState(startInEditMode); // Use startInEditMode prop to initialize edit state
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
    const [currentContact, setCurrentContact] = useState(contact);
    const navigate = useNavigate();
    const { updateContact } = useLeadCaptureContext(); // Use the context


    const handleUpdateContact = (updatedContact) => {
        // Update the current contact state with the new details
        setCurrentContact(updatedContact);

        // Update the contact in the global contacts context
        updateContact(updatedContact);

        // Exit edit mode
        setIsEditing(false);
    };

    // Function to handle edit click from any source
    const handleEditClick = () => {
        setIsEditing(true);
    };

    // Function to handle successful deletion
    const handleDeleteComplete = () => {
        // Navigate back to the contacts list with a flag indicating deletion happened
        if (onDeleteComplete) {
            onDeleteComplete();
        } else {
            onBack();
        }
    };

    // Function to handle email click
    const handleEmailClick = () => {
        setIsEmailModalOpen(true);
    };

    // Function to handle CSV export
    const handleExportToCSV = () => {
        if (currentContact) {
            try {
                ExportUtils.exportSingleLead(currentContact);
            } catch (error) {
                // Error handling for CSV export
            }
        }
    };

    // Function to handle Excel export
    const handleExportToExcel = () => {
        if (currentContact) {
            try {
                ExportUtils.exportSingleLeadToExcel(currentContact);
            } catch (error) {
                // Error handling for Excel export
            }
        }
    };

    // Prepare contact data in the format expected by downloadVCard
    const prepareContactData = () => {
        return {
            contact_info: {
                fullName: currentContact.leadName,
                email: currentContact.leadEmail,
                phoneNumber: currentContact.leadPhoneNumber,
                company: currentContact.leadCompany,
                jobTitle: currentContact.leadJobTitle,
                location: currentContact.leadAddress
            },
            bio: currentContact.leadNotes,
            profilePictureUrl: currentContact.profilePictureUrl
        };
    };

    const actionButtons = [
        {
            icon: EditOutlinedIcon,
            text: "Edit",
            onClick: handleEditClick
        },
        {
            icon: SaveAltOutlinedIcon,
            text: "Save as contact",
            onClick: () => {
                // Prepare contact data for VCF download
                const contactData = prepareContactData();
                downloadVCard(contactData);
            }
        },
        {
            icon: EmailOutlinedIcon,
            text: "Email Lead",
            onClick: handleEmailClick
        }
    ];

    return (
        <Box sx={{
            width: '100%',
            height: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <ContactDetailMenu
                contact={currentContact}
                onBack={onBack}
                onEditClick={handleEditClick}
                onDeleteComplete={handleDeleteComplete}
            />
            <Box sx={{
                bgcolor: '#ffffff',
                borderRadius: 4,
                m: 2,
                p: 0,
                display: 'flex',
                flex: 1,
                overflow: 'auto'
            }}>
                <Box sx={{
                    width: '100%',
                    p: 6,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {!isEditing && (
                        <Box sx={{
                            display: 'flex',
                            gap: 3,
                            mb: 2,
                            flexShrink: 0
                        }}>
                            {actionButtons.map((button, index) => (
                                <WhiteButtonWithIcon
                                    key={index}
                                    icon={button.icon}
                                    text={button.text}
                                    onClick={button.onClick || (() => { })}
                                    padding="8px 48px"
                                    data-edit-contact-button={button.text === "Edit" ? true : undefined}
                                />
                            ))}
                        </Box>
                    )}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {isEditing ? (
                            <ContactDetailsEdit
                                contact={currentContact}
                                onCancel={() => setIsEditing(false)}
                                onUpdate={handleUpdateContact}
                            />
                        ) : (
                            <ContactDetailCard contact={currentContact} />
                        )}
                    </Box>
                </Box>
            </Box>

            <SendLeadEmailModal
                open={isEmailModalOpen}
                onClose={() => setIsEmailModalOpen(false)}
                contact={currentContact}
            />


        </Box>
    );
};

export default ContactDetailsContainer;